import { useCallback, useContext, useMemo, useState } from 'react'

import { useSelector } from 'react-redux'
import moment from 'moment'
import { isArray, uniqBy } from 'lodash'

import Settings from './Settings'
import { selectUser } from '../../../../reducers/UserReducer'
import { TimePeriod, PeriodTypes } from '../../../../utils/timePeriods'
import { InternshipsSettings } from '../../../../Components/antd/Tables/DragTable/OverlaySettings'
import { useLocalStorage } from '../../../../hooks/UseLocalStorage'
import LOCAL_STORAGE_KEYS, { localStorage } from '../../../../utils/localStorage'
import { firstOrDefault, formatAsArray, getEntityOrNull, getIdOrNull, isEmptyArray, isNonEmptyArray, isObject } from '../../../../utils'
import useRequest from '../../../../hooks/useRequest'
import { getSchoolPeriods } from '../../../../utils/api/schoolPeriods'
import { getStudentInternships } from '../../../../utils/api/student'
import { onInfo } from '../../../../utils/apiHelper'
import { selectTranslate } from '../../../../reducers/LocaleReducer/LocalReducer'
import { InternshipStates, InternshipStatesAsString, OrderBy } from '../../../../utils/constants'
import { SchoolPeriod } from '../../../../utils/entities/schoolPeriod'
import { Order, QueryParams } from '../../../../utils/entities/queries'
import { getDifference, QuotasMap, selectionToInternships } from '../../../../Components/shared/Quotas/utils'
import PlacesOccupancyHeader from './PlacesOccupancyHeader'
import GoToInternshipsBanner from './GoToInternshipsBanner'
import PlacesOccupancyTable, { DEFAULT_SELECTED_VALUES, DisplayTypes } from './PlacesOccupancyTable'
import InternshipFormModal from '../../../../Providers/Forms/InternshipForm/InternshipFormModal'
import { Internship, InternshipProperties } from '../../../../utils/entities/internship'
import { Actions } from '../../../../utils/events'
import HighlightedColumnsProvider from '../../../../Providers/Quotas/HighlightedColumnsProvider'
import InternshipToolBar from './InternshipToolBar'
import { arePeriodsOverlapping } from '../../../../utils/momentjs'
import { Modes, usePlacesMaps } from '../../../../hooks/quotas/usePlacesMaps'
import { InternshipFilters, SerializationContexts } from '../../../../utils/api/internship'
import { InstitutionContext } from '../../../../Providers/InstitutionProvider'
import Expandable from '../../../../Components/shared/Expandable'
import { DEFAULT_ZOOM, ZOOM_LEVELS } from './ZoomControl'

import '../../../../assets/quota-allocation.scss'

const KEYS_TO_VALIDATE = [
  InternshipProperties.SCHOOL,
  InternshipProperties.START_DATE,
  InternshipProperties.END_DATE,
  InternshipProperties.SECTOR
]

export const STEP = 10
const SCROLL_LOAD_BUFFER_PX = 200

const EXCLUDED_STATES = [InternshipStates.REFUSED, InternshipStates.CANCELED]
const EXCLUDED_STATES_AS_STRING = [InternshipStatesAsString.REFUSED, InternshipStatesAsString.CANCELED]

const isExcludedState = internship => EXCLUDED_STATES.includes(internship.state) || EXCLUDED_STATES_AS_STRING.includes(internship.state)

const STUDENT_INTERNSHIPS_CONTEXTS = ['sector', 'institution']

const PlacesOccupancyView = () => {
  const { sectorsOrderByName } = useContext(InstitutionContext)

  const t = useSelector(selectTranslate)
  const user = useSelector(selectUser)

  const [period, setPeriod] = useState(new TimePeriod(moment(), 26, PeriodTypes.WEEK))
  const [isYearly, setIsYearly] = useState(false)
  const [schools, setSchools] = useState([])
  const [selectedSections, setSelectedSections] = useState([])
  const [sectors, setSectors] = useState([])
  const [activeSectors, setActiveSectors] = useState([])
  const [selectedData, setSelectedData] = useState(DEFAULT_SELECTED_VALUES)
  const [selectedStudent, setSelectedStudent] = useState(null)
  const [studentInternships, setStudentInternships] = useState([])
  const [sectorsBeforeStudent, setSectorsBeforeStudent] = useState(null)

  const [page, setPage] = useState(1)

  const [isHostpitalDepartmentViewToggled, setIsHospitalDepartmentViewToggled] = useLocalStorage(
    LOCAL_STORAGE_KEYS.PLACES_OCCUPANCY_VIEW.HOSPITAL_DEPARTMENT_VIEW_TOGGLED, false
  )

  const [overlaySettings, setOverlaySettings] = useLocalStorage(
    LOCAL_STORAGE_KEYS.QUOTAS_TABLE.OVERLAY_SETTINGS,
    new InternshipsSettings(),
    value => new InternshipsSettings(value)
  )

  const [zoom, setZoom] = useLocalStorage(
    LOCAL_STORAGE_KEYS.PLACES_OCCUPANCY_VIEW.ZOOM,
    DEFAULT_ZOOM,
    value => ZOOM_LEVELS.includes(value) ? value : DEFAULT_ZOOM
  )

  const tableConfig = useMemo(() => ({ overlay: overlaySettings, zoom }), [overlaySettings, zoom])
  const scrollTopThreshold = 20

  const holidays = useRequest({
    request: getSchoolPeriods,
    enabled: schools.length === 1,
    parameters: useMemo(() => ({
      filters: { school: getIdOrNull(schools[0]) },
      order: new Order({ by: OrderBy.START_DATE })
    }), [schools[0]]),
    select: useCallback(holidays => holidays.map(holiday => getIdOrNull(holiday) ? new SchoolPeriod(holiday) : null), [])
  })

  const hasStudentFilter = useMemo(() => isObject(selectedStudent), [selectedStudent])
  const loadedSections = useMemo(() => hasStudentFilter ? [] : selectedSections, [hasStudentFilter, selectedSections])

  const internshipsParams = useMemo(() => new QueryParams({
    filters: new InternshipFilters({
      schools: hasStudentFilter ? [] : schools,
      sectors: [-1],
      states: Object.values(InternshipStates).filter(state => !EXCLUDED_STATES.includes(state))
    }),
    contexts: [SerializationContexts.NOTES],
    paginate: false
  }), [schools, hasStudentFilter])

  const sectorsMaps = usePlacesMaps({
    mode: Modes.SECTORS,
    isModeActive: !isHostpitalDepartmentViewToggled,
    selectedEntities: activeSectors,
    internshipsParams,
    schools,
    overlaySettings
  })

  const config = useMemo(() => {
    const entities = sectors
    const activeEntities = activeSectors
    const internshipsMap = sectorsMaps.internships
    const setEntities = setSectors
    const setActiveEntities = setActiveSectors
    const loadData = sectorsMaps.loadData
    const setInternshipsMap = sectorsMaps.setInternships

    return {
      entities,
      activeEntities,
      internshipsMap,
      overlay: overlaySettings,
      loadData,
      setEntities,
      setActiveEntities,
      setInternshipsMap
    }
  }, [
    activeSectors, overlaySettings, sectors, sectorsMaps.internships,
    sectorsMaps.loadData, sectorsMaps.setInternships
  ])

  const handleEntityChange = useCallback((selected, shouldAdd) => {
    let newEntities = []

    if (isArray(selected)) {
      const newActive = selected.slice(0, STEP)
      newEntities = selected

      config.setActiveEntities(newActive)

      setPage(1)

      config.loadData(newActive, period, true, loadedSections)
    } else if (shouldAdd) {
      newEntities = config.entities.concat(selected)

      if (page * STEP >= newEntities.length) {
        config.setActiveEntities(newEntities)

        config.loadData([selected], period, true, loadedSections)
      }
    } else {
      newEntities = config.entities.filter(s => s.id !== selected.id)

      const newActive = newEntities.slice(0, page * STEP)

      config.setActiveEntities(newActive)

      const diff = getDifference(newActive, config.activeEntities)

      if (diff.length > 0) {
        config.loadData(diff, period)
      }
    }

    config.setEntities(newEntities)
    setSectorsBeforeStudent(null)
  }, [config.entities, config.activeEntities, loadedSections, page, period, config.loadData, config.setActiveEntities, config.setEntities])

  const handleDateChange = useCallback(date => {
    const newPeriod = new TimePeriod(date, period.quantity, PeriodTypes.WEEK)
    const newActive = config.entities.slice(0, STEP)

    setSelectedData(DEFAULT_SELECTED_VALUES)
    setPage(1)
    setPeriod(newPeriod)
    config.setActiveEntities(newActive)
    config.loadData(newActive, newPeriod)
  }, [config.entities, period, config.loadData, config.setActiveEntities])

  const handlePeriodChange = useCallback(quantity => {
    const newPeriod = new TimePeriod(period.startDate, quantity, PeriodTypes.WEEK)
    const newActive = config.entities.slice(0, STEP)

    setSelectedData(DEFAULT_SELECTED_VALUES)
    setPage(1)
    setPeriod(newPeriod)
    config.setActiveEntities(newActive)
    config.loadData(newActive, newPeriod)
  }, [config.entities, period, config.loadData, config.setActiveEntities])

  const handleSectionsChange = useCallback(sections => {
    setSelectedSections(sections)
    config.loadData(config.activeEntities, period, false, hasStudentFilter ? [] : sections)
  }, [activeSectors, hasStudentFilter, period, config.loadData, config.activeEntities])

  const showStudentInternships = useCallback((student, internships, studentSectors, closestStartDate) => {
    const newActive = studentSectors.slice(0, STEP)
    const newPeriod = new TimePeriod(closestStartDate, period.quantity, PeriodTypes.WEEK)

    setSectorsBeforeStudent(memorized => memorized ?? config.entities)
    setSelectedStudent(student)
    setStudentInternships(internships)
    setSelectedData(DEFAULT_SELECTED_VALUES)
    setPage(1)
    setPeriod(newPeriod)
    config.setEntities(studentSectors)
    config.setActiveEntities(newActive)
    config.loadData(newActive, newPeriod, true, [], { schools: [] })
  }, [config.entities, config.loadData, config.setEntities, config.setActiveEntities, period.quantity])

  /** @param schoolsFilter Schools to load back with, the caller changing them in the same go stating them here. */
  const clearStudentFilter = useCallback((schoolsFilter = schools) => {
    setSelectedStudent(null)
    setStudentInternships([])

    if (sectorsBeforeStudent === null) {
      return
    }

    const newActive = sectorsBeforeStudent.slice(0, STEP)

    setSectorsBeforeStudent(null)
    setSelectedData(DEFAULT_SELECTED_VALUES)
    setPage(1)
    config.setEntities(sectorsBeforeStudent)
    config.setActiveEntities(newActive)
    config.loadData(newActive, period, true, selectedSections, { schools: schoolsFilter })
  }, [sectorsBeforeStudent, config.loadData, config.setEntities, config.setActiveEntities, period, schools, selectedSections])

  const handleStudentChange = useCallback(student => {
    const newStudent = getEntityOrNull(student)

    if (newStudent === null) {
      clearStudentFilter()

      return
    }

    setSelectedStudent(newStudent)

    getStudentInternships(user, newStudent, { contexts: STUDENT_INTERNSHIPS_CONTEXTS }).then(json => {
      const today = moment()

      const displayable = formatAsArray(json?.data)
        .map(i => new Internship(i))
        .filter(i => !isExcludedState(i))
        .reduce((acc, internship) => {
          const sector = sectorsOrderByName.find(s => getIdOrNull(s) === getIdOrNull(internship.sector))

          if (isObject(sector)) {
            acc.push({ internship, sector })
          }

          return acc
        }, [])

      if (isEmptyArray(displayable)) {
        setSelectedStudent(null)
        onInfo(t('places_occupancy_view.students_filter.no_internship'))

        return
      }

      const closest = displayable.reduce((a, b) => (
        Math.abs(a.internship.startDate.diff(today)) <= Math.abs(b.internship.startDate.diff(today)) ? a : b
      ))
      const sorted = [...displayable].sort((a, b) => a.internship.startDate.diff(b.internship.startDate))

      showStudentInternships(newStudent, sorted, uniqBy(displayable.map(d => d.sector), 'id'), closest.internship.startDate)
    })
  }, [user, t, sectorsOrderByName, clearStudentFilter, showStudentInternships])

  const handleSchoolsChange = useCallback(newSchools => {
    setSchools(newSchools)
    clearStudentFilter(newSchools)
  }, [clearStudentFilter])

  const handleSelectSetting = useCallback((setting, isSelected) => {
    setOverlaySettings(current => current.clone({ [setting]: isSelected }))
  }, [])

  const handleScroll = useCallback(({ target }) => {
    const isAtBottom = target.scrollTop + target.offsetHeight >= (target.scrollHeight - scrollTopThreshold)

    if (!isAtBottom) {
      return
    }

    setPage(currentPage => {
      if ((currentPage * STEP) >= config.entities.length) {
        return currentPage
      }

      const nextPage = currentPage + 1
      const toBeLoaded = config.entities.slice(currentPage * STEP, nextPage * STEP)

      config.setActiveEntities(currentEntities => [...currentEntities, ...toBeLoaded])
      config.loadData(toBeLoaded, period)

      return nextPage
    })
  }, [config.entities, page, period, config.loadData, config.setActiveEntities, scrollTopThreshold])

  const handleInternshipsChange = useCallback((...params) => {
    const newInternshipsMap = { ...config.internshipsMap }
    let newSelectedData = [...selectedData.values]

    for (let iItemActionPairs = 0; iItemActionPairs < params.length; iItemActionPairs += 2) {
      const internship = params[iItemActionPairs]
      const action = params[iItemActionPairs + 1]
      const isInPeriod = arePeriodsOverlapping(internship, period)

      let newMap = newInternshipsMap[internship.sector.id]

      if (!isNonEmptyArray(newMap)) {
        newMap = []
      }

      if (action !== Actions.DELETE && !isInPeriod) {
        newMap = newMap.filter(i => i.id !== internship.id)
        newSelectedData = newSelectedData.filter(d => d.internship.id !== internship.id)
      } else if (action === Actions.CREATE && isInPeriod) {
        newMap = newMap.concat(internship)
      } else if (action === Actions.UPDATE) {
        const currentList = newMap
        const isFound = currentList.some(i => i.id === internship.id)

        let updatedList = currentList
          .filter(i => !EXCLUDED_STATES.includes(i.state))
          .map(i => i.id === internship.id ? internship : i)

        if (!isFound && isInPeriod) {
          updatedList = updatedList.concat(internship)
        }

        newMap = updatedList
        newSelectedData = newSelectedData.map(d => d.internship.id === internship.id ? { ...d, internship: new Internship(internship) } : d)
      } else if (action === Actions.DELETE) {
        newMap = newMap.filter(i => i.id !== internship.id)
        newSelectedData = newSelectedData.filter(d => d.internship.id !== internship.id)
      } else if (action === Actions.RELOAD) {
        newMap = [...newMap]
      }

      newInternshipsMap[internship.sector.id] = newMap
    }
    config.setInternshipsMap(newInternshipsMap)
    setSelectedData({ ...selectedData, values: newSelectedData })
  }, [config.internshipsMap, selectedData, config.setInternshipsMap])

  const seed = useCallback(sector => ({
    institution: getEntityOrNull(user.institutions[0]),
    sector: getEntityOrNull(sector)
  }), [user.institutions[0]])

  const handleResetGravity = useCallback(() => {
    Object.keys(config.internshipsMap).forEach(sectorId => {
      config.internshipsMap[sectorId].forEach(internship => {
        localStorage.removeValue(LOCAL_STORAGE_KEYS.PLACES_OCCUPANCY_VIEW.INTERNSHIPS_MATRIX, internship.id)
      })
    })

    setActiveSectors(sectors => [...sectors])
  }, [config.internshipsMap])

  return (
    <div className='places-occupancy-view-container flex-row h-100 w-100'>
      <InternshipFormModal keysToValidate={KEYS_TO_VALIDATE} sectors={sectorsOrderByName}>
        <Expandable onExpandChange={() => setTimeout(() => setActiveSectors(prev => [...prev]), 20)}>
          <Settings
            isHostpitalDepartmentViewToggled={isHostpitalDepartmentViewToggled}
            schools={schools}
            selectedSections={selectedSections}
            selectedSectors={sectors}
            selectedStudent={selectedStudent}
            onSelectEntity={handleEntityChange}
            onSelectSchools={handleSchoolsChange}
            onSelectSections={handleSectionsChange}
            onSelectStudent={handleStudentChange}
          />
        </Expandable>
        <div className='places-occupancy-tables-container flex-column h-100'>
          <div className='places-occupancy-tables-header h-5 w-100 pd-btm-8'>
            <PlacesOccupancyHeader
              addDisabled={isEmptyArray(config.activeEntities)}
              period={period}
              isYearly={isYearly}
              overlaySettings={overlaySettings}
              isHostpitalDepartmentViewToggled={isHostpitalDepartmentViewToggled}
              school={schools.length === 1 ? firstOrDefault(schools) : null}
              zoom={zoom}
              onDateChange={handleDateChange}
              onPeriodChange={handlePeriodChange}
              onInternshipChange={handleInternshipsChange}
              onIsYearlyChange={setIsYearly}
              onOverlaySettingsSelection={handleSelectSetting}
              onHospitalDepartmentViewToggled={setIsHospitalDepartmentViewToggled}
              onResetGravity={handleResetGravity}
              onZoomChange={setZoom}
            />
          </div>
          <GoToInternshipsBanner
            internships={studentInternships}
            period={period}
            onFocus={internship => handleDateChange(internship.startDate)}
          />
          <div className='places-occupancy-tables-content h-95 w-100 scroll-y' onScroll={handleScroll}>
            <HighlightedColumnsProvider>
              {activeSectors.map(s => (
                <div key={s.id} className='flex-column w-100 pd-btm-12'>
                  <h3><strong>{s.deptName ? s.deptName + ' ─ ' : ''}</strong>{s.name}</h3>
                  <PlacesOccupancyTable
                    config={tableConfig}
                    period={period}
                    school={schools.length === 1 ? firstOrDefault(schools) : null}
                    holidayPeriods={holidays.data}
                    internships={sectorsMaps.internships[s.id] ?? []}
                    seed={seed(s)}
                    selectedData={selectedData ?? DEFAULT_SELECTED_VALUES}
                    type={isHostpitalDepartmentViewToggled ? DisplayTypes.SECTION : DisplayTypes.SCHOOL}
                    quotasMap={sectorsMaps.quotas[s.id] ?? new QuotasMap()}
                    sectionGroups={sectorsMaps.sectionGroups[s.id] ?? []}
                    studentPathways={sectorsMaps.studentPathways[s.id] ?? []}
                    selectedStudent={selectedStudent}
                    onChange={handleInternshipsChange}
                    onSelect={setSelectedData}
                  />
                </div>
              ))}
              {page * STEP < config.entities.length && (
                <div aria-hidden='true' style={{ height: SCROLL_LOAD_BUFFER_PX, width: '100%' }} />
              )}
            </HighlightedColumnsProvider>
          </div>
          {isNonEmptyArray(selectedData.values) && (
            <InternshipToolBar
              date={selectedData.startDate}
              internships={selectionToInternships(selectedData)}
              onChange={handleInternshipsChange}
              onUnselect={() => setSelectedData(DEFAULT_SELECTED_VALUES)}
            />
          )}
        </div>
      </InternshipFormModal>
    </div>
  )
}

export default PlacesOccupancyView
